/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, Container, Box } from '@tishman/components';
import { useStaticQuery, graphql } from 'gatsby';
import React, { useMemo } from 'react';
import invariant from 'invariant';

import IntroText from '../components/IntroText';
import { HolidayHoursAndAddress } from '../components/Holidays/HolidayHoursAndAddress';

import {
  getHoursProps,
  getLocationProps,
  getContactsInfoProps,
  SanityHourDataProps,
  LocationProps,
} from './utils';

import type { ComponentPropsWithoutRef } from 'react';

const HOLIDAYS_HERO_QUERY = graphql`
  query HolidayHero {
    sanityAttractionHoliday(
      _id: { eq: "theHolidays" }
      _type: { eq: "attraction.holiday" }
    ) {
      heroCTA {
        title
      }
      hour {
        hourText
      }
      contactsInfo {
        type
        value
      }
      location {
        title
        address1
        address2
      }
    }
    dataJson(jsonId: { eq: "holidays" }) {
      hero {
        contactLinkUrl
      }
    }
    treeLightingData: allSanityEventTreeLighting(limit: 1) {
      nodes {
        countdownMessage {
          targetDate
          message
        }
      }
    }
  }
`;

const HolidaysHeroBlock = (
  props: ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element => {
  const { dataJson, sanityAttractionHoliday, treeLightingData } =
    useStaticQuery<Queries.HolidayHeroQuery>(HOLIDAYS_HERO_QUERY);

  invariant(dataJson, 'Holidays JSON data is required!');

  const holidaysHeroProps = useMemo(() => {
    if (!sanityAttractionHoliday)
      throw new Error('Expected valid Holidays sanity data');

    if (!sanityAttractionHoliday?.heroCTA)
      throw new Error('Expected valid Holidays hero text');
    if (!sanityAttractionHoliday?.heroCTA?.title)
      throw new Error('Expected valid Holidays hero text title');

    const heroText = {
      title: sanityAttractionHoliday.heroCTA.title,
    };

    if (!sanityAttractionHoliday?.hour)
      throw new Error('Expected valid Holidays hour data');

    const hours = getHoursProps(
      sanityAttractionHoliday.hour as SanityHourDataProps,
    );

    const location = getLocationProps(
      sanityAttractionHoliday?.location as LocationProps,
    );

    if (!sanityAttractionHoliday?.contactsInfo)
      throw new Error('Expected Holidays contacts info');

    const contactsInfo = sanityAttractionHoliday?.contactsInfo.map((contact) =>
      getContactsInfoProps(contact as Queries.SanityContactInfo),
    );

    return {
      heroText,
      location,
      contactsInfo,
      hours,
      contactLinkUrl: dataJson.hero.contactLinkUrl,
      countdownTargetDate:
        treeLightingData.nodes[0].countdownMessage?.targetDate ?? '',
      countdownMessage:
        treeLightingData.nodes[0].countdownMessage?.message ?? '',
    };
  }, [sanityAttractionHoliday, dataJson, treeLightingData]);

  return (
    <Section data-section="HolidaysHero" theme="Rock Center Navy" {...props}>
      <Container>
        <Container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: ['column', null, 'row'],

            pt: 6,
            pb: [6, null, 6],
          }}
        >
          <IntroText
            maxWidth={430}
            showMobileCTAs
            sx={{ mb: 4 }}
            {...holidaysHeroProps.heroText}
          />
          <Box>
            <HolidayHoursAndAddress
              contactLinkUrl={holidaysHeroProps.contactLinkUrl ?? ''}
              contactsInfo={holidaysHeroProps.contactsInfo}
              hours={holidaysHeroProps.hours}
              location={holidaysHeroProps.location}
              row
            />
          </Box>
        </Container>
      </Container>
    </Section>
  );
};

export default HolidaysHeroBlock;
